import { useEffect } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "../../lib/usePageMeta";
import {
  Phone,
  MessageSquareText,
  Video,
  Mail,
  ShieldCheck,
  Heart,
  Users,
  Eye,
  EyeOff,
  ArrowRight,
  AlertTriangle,
  Sparkles,
  Building2,
} from "lucide-react";
import AccessModelCallout from "../../components/public/AccessModelCallout";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1512428559087-560fa5ceab42?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

const CALLACHAP_VIDEO_URL =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_ccchaps-care/artifacts/08ayo8x5_ccc-callachap_838.mp4";

const METHOD_CARDS = [
  {
    icon: Phone,
    title: "Call a Chaplain",
    copy: "Reach the on-call CCCHAPS chaplain team by phone, routed through your organization's CALLACHAP number.",
    testId: "landing-method-call",
  },
  {
    icon: MessageSquareText,
    title: "Text a Chaplain",
    copy: "Send a text; a chaplain responds. Text is not a live-chat channel.",
    testId: "landing-method-text",
  },
  {
    icon: Video,
    title: "Remote Support",
    copy: "Connect through available remote-support options when included in your organization's care arrangement.",
    testId: "landing-method-video",
  },
  {
    icon: Mail,
    title: "Send a Confidential Message",
    copy: "Send a confidential support request — a chaplain follows up using your preferred contact method.",
    testId: "landing-method-message",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Open CALLACHAP",
    copy: "Employees access their organization's CALLACHAP page from a phone, tablet, or computer.",
  },
  {
    n: "02",
    title: "Choose Support",
    copy: "Call, text, send a confidential message, or use available remote-support options.",
  },
  {
    n: "03",
    title: "Connect Privately",
    copy: "A CCCHAPS chaplain responds — live for phone calls routed through the organization's CALLACHAP number, and by follow-up for text, confidential-message, and remote-support requests. Chaplain support is 24/7 on-call.",
  },
  {
    n: "04",
    title: "Measure Reach",
    copy:
      "Organizations receive appropriate aggregate, non-identifying utilization information — no employee names, conversation details, or reasons for seeking support.",
  },
];

const CAN_SEE = [
  "Number of employees supported",
  "Total support connections",
  "General service utilization",
  "Usage trends over time",
  "Approved aggregate program information",
];

const CANNOT_SEE = [
  "Employee names",
  "Employee contact information",
  "Individual reasons for seeking support",
  "Messages",
  "Conversation notes",
  "Chaplain conversation content",
];

const ORGS = [
  { name: "BALM", tag: "Demonstration Company", accent: true },
  { name: "Company A", tag: "Future Client" },
  { name: "Company B", tag: "Future Client" },
];

export default function CallAChapPage() {
  usePageMeta({
    title: "CALLACHAP — Confidential Employee Chaplain Support | California Corporate Chaplains",
    description:
      "CALLACHAP is CCCHAPS' 24/7 on-call employee chaplain access — confidential phone, text, remote-support, and confidential-message pathways for employees of enrolled California organizations.",
  });

  return (
    <main data-testid="callachap-page" className="bg-white text-slate-900">
      {/* ============================ HERO ============================ */}
      <section
        data-testid="callachap-hero"
        className="relative overflow-hidden bg-gradient-to-b from-[#F5F8FC] via-white to-white"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-12 gap-10 lg:gap-14 items-center relative">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="flex items-center gap-3 mb-6">
              <img
                src="/callachap-logo.png"
                alt="CALLACHAP"
                className="h-11 md:h-14 w-auto"
                data-testid="callachap-hero-logo"
              />
            </div>
            <p className="text-xs uppercase tracking-[0.24em] text-[#4A6FA5] font-semibold mb-4">
              CALLACHAP • Confidential Employee Support
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-slate-900">
              Care is one click away.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-700 max-w-xl leading-relaxed">
              CALLACHAP gives employees a simple, private way to connect with a chaplain by phone,
              text, confidential message, or available remote-support options — when life gets hard.
              Chaplain support is available 24/7 on-call; response times vary by channel and by
              organization configuration. CALLACHAP is included with CCCHAPS chaplaincy service —
              employees do not pay to use it.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/callachap/demo/balm"
                data-testid="landing-cta-employee-demo"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider shadow-sm transition-colors"
              >
                View Employee Demo <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
              </Link>
              <Link
                to="/callachap/demo/employer"
                data-testid="landing-cta-employer-demo"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white border border-slate-300 hover:border-[#4A6FA5] hover:text-[#4A6FA5] text-slate-800 text-sm font-semibold uppercase tracking-wider transition-colors"
              >
                View Employer Dashboard Demo <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
              </Link>
            </div>

            <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-800">
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2.2} aria-hidden="true" />
              Demonstration Company: BALM
            </div>
            <p className="mt-3 text-xs text-slate-500 max-w-xl leading-relaxed">
              This demonstration uses sample information only. No real employee support requests
              are submitted.
            </p>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-tr from-[#4A6FA5]/10 to-transparent rounded-3xl blur-2xl" aria-hidden="true" />
              {/* Phone-shaped mockup */}
              <div className="relative mx-auto max-w-[320px] rounded-[2.2rem] bg-slate-900 p-3 shadow-2xl">
                <div className="relative rounded-[1.7rem] overflow-hidden bg-white aspect-[9/17]">
                  <img
                    src={HERO_IMAGE}
                    alt="Employee using CALLACHAP on a phone"
                    className="absolute inset-0 w-full h-full object-cover opacity-60"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-[#4A6FA5]/85 to-[#3B597B]/90" aria-hidden="true" />
                  <div className="relative h-full flex flex-col p-5 text-white">
                    <div className="flex items-center gap-2">
                      <img src="/callachap-logo.png" alt="" className="h-7 w-auto brightness-0 invert" />
                    </div>
                    <p className="mt-6 text-[10px] uppercase tracking-[0.22em] text-white/70 font-semibold">
                      For BALM Employees
                    </p>
                    <h2 className="mt-2 font-heading text-2xl leading-tight tracking-tight">
                      How can we support you today?
                    </h2>
                    <div className="mt-6 grid grid-cols-2 gap-2.5 flex-1 content-start">
                      {[
                        { icon: Phone, label: "Call" },
                        { icon: MessageSquareText, label: "Text" },
                        { icon: Mail, label: "Message" },
                        { icon: Video, label: "Remote" },
                      ].map((m) => (
                        <div key={m.label} className="flex flex-col items-center justify-center gap-1.5 p-3 rounded-xl bg-white/10 backdrop-blur border border-white/15">
                          <m.icon className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
                          <span className="text-[11px] font-semibold uppercase tracking-wider">{m.label}</span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-4 text-[10px] text-white/70 text-center">
                      Confidential support • Employee-centered
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ VIDEO ============================ */}
      <section
        data-testid="callachap-video-section"
        className="py-16 md:py-24 border-t border-slate-100"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold mb-3">
              How it works
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900">
              See How CALLACHAP Works
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              See how CALLACHAP is designed to give employees convenient access to workplace
              chaplain support through phone, text, confidential message, and available remote-support options.
            </p>
          </div>
          <div className="mt-10">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-900 aspect-video">
              <video
                data-testid="callachap-video"
                controls
                preload="metadata"
                playsInline
                className="w-full h-full block"
                aria-label="CALLACHAP concept video: how employees can reach a chaplain by phone, text, confidential message, or available remote-support options"
              >
                <source src={CALLACHAP_VIDEO_URL} type="video/mp4" />
                Your browser does not support the video element.
              </video>
              <span
                data-testid="callachap-video-badge"
                className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[10px] uppercase tracking-[0.22em] font-semibold text-[#3B597B] shadow"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-[#4A6FA5]" aria-hidden="true" />
                CALLACHAP Concept Demo
              </span>
            </div>
            <p
              data-testid="callachap-video-disclaimer"
              className="mt-4 text-sm text-slate-500 max-w-3xl leading-relaxed"
            >
              This video demonstrates the CALLACHAP employee-support concept. Communication
              features shown in this demonstration are not currently active through this page.
            </p>
          </div>
        </div>
      </section>

      {/* ============================ METHOD CARDS ============================ */}
      <section className="py-16 md:py-24 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold mb-3">
              Four ways to connect
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Support, on the employee's terms.
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {METHOD_CARDS.map((m) => (
              <article
                key={m.title}
                data-testid={m.testId}
                className="group p-6 rounded-2xl bg-white border border-slate-200 hover:border-[#4A6FA5]/50 hover:shadow-lg transition-all duration-200"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#F0F4F8] text-[#4A6FA5] group-hover:bg-[#4A6FA5] group-hover:text-white transition-colors mb-4">
                  <m.icon className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
                </span>
                <h3 className="font-heading text-lg tracking-tight text-slate-900">{m.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{m.copy}</p>
              </article>
            ))}
          </div>

          <AccessModelCallout
            variant="default"
            className="mt-10"
            testId="callachap-access-model-callout"
          />
        </div>
      </section>

      {/* ============================ HOW IT WORKS (4 STEPS) ============================ */}
      <section
        data-testid="callachap-steps"
        className="py-16 md:py-24 bg-[#FAFBFD] border-t border-slate-100"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold mb-3">
              How it works
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              From open to connect in four steps.
            </h2>
          </div>
          <ol className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {STEPS.map((s) => (
              <li
                key={s.n}
                className="rounded-2xl p-6 bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow"
              >
                <p className="font-heading text-3xl text-[#4A6FA5] tracking-tight">{s.n}</p>
                <h3 className="mt-3 font-heading text-lg tracking-tight text-slate-900">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.copy}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ============================ AGGREGATE METRICS PREVIEW ============================ */}
      <section className="py-16 md:py-24 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold mb-3">
                Utilization at a glance
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">
                Aggregate reach — never identities.
              </h2>
              <p className="mt-4 text-base text-slate-600 leading-relaxed">
                Employers receive appropriate aggregate utilization information. They never
                receive employee names, contact details, or any information about individual
                conversations.
              </p>
              <div className="mt-6 rounded-xl p-4 bg-[#F0F4F8] border border-[#4A6FA5]/15 text-sm text-[#3B597B]">
                <div className="flex items-start gap-2">
                  <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2.2} aria-hidden="true" />
                  <div>
                    <p><strong>Employees Supported</strong> = unique employees who used CALLACHAP.</p>
                    <p className="mt-1"><strong>Support Connections</strong> = total employee/chaplain contacts. One employee may have multiple support connections.</p>
                  </div>
                </div>
              </div>
              <Link
                to="/callachap/demo/employer"
                data-testid="landing-preview-view-dashboard"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#4A6FA5] hover:text-[#3B597B]"
              >
                View the full employer dashboard demo <ArrowRight className="w-4 h-4" strokeWidth={2.2} />
              </Link>
            </div>
            <div className="lg:col-span-7">
              <div
                data-testid="callachap-aggregate-preview"
                className="rounded-3xl p-6 md:p-8 bg-gradient-to-br from-[#3B597B] via-[#4A6FA5] to-[#5B7FB2] text-white shadow-2xl"
              >
                <div className="flex items-center justify-between">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-white/70 font-semibold">
                    BALM • This Quarter
                  </p>
                  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur text-[10px] uppercase tracking-[0.22em] font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-300" aria-hidden="true" />
                    Demonstration Data
                  </span>
                </div>
                <div className="mt-6 flex items-center gap-4">
                  <span className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-white/15 backdrop-blur">
                    <Heart className="w-8 h-8 text-rose-200" strokeWidth={2.2} aria-hidden="true" />
                  </span>
                  <div>
                    <div className="font-heading text-6xl md:text-7xl leading-none tracking-tight">32</div>
                    <p className="mt-2 text-xs uppercase tracking-[0.22em] font-semibold text-white/80">Employees Supported</p>
                  </div>
                </div>
                <div className="mt-8 grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-2xl bg-white/10 backdrop-blur border border-white/15">
                    <div className="font-heading text-3xl tracking-tight">57</div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/75 font-semibold mt-1">Support Connections</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-white/10 backdrop-blur border border-white/15">
                    <div className="font-heading text-3xl tracking-tight">0</div>
                    <p className="text-[10px] uppercase tracking-[0.2em] text-white/75 font-semibold mt-1">Private Conversations Shared</p>
                  </div>
                  <div className="p-4 rounded-2xl bg-white/10 backdrop-blur border border-white/15 flex flex-col justify-center">
                    <p className="text-xs uppercase tracking-[0.2em] text-white/75 font-semibold">This Quarter</p>
                    <p className="text-xs text-white/60 mt-1">Reporting window</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ PRIVACY SPLIT ============================ */}
      <section
        data-testid="callachap-privacy-split"
        className="py-20 md:py-28 bg-gradient-to-br from-[#3B597B] via-[#4A6FA5] to-[#5B7FB2] text-white"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-white/70 font-semibold mb-3">
              Privacy by design
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              See the care. Protect the conversation.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-white/85 leading-relaxed">
              CALLACHAP separates confidential employee chaplain support from employer-facing
              program reporting. Employers may see appropriate aggregate utilization information —
              they do not see individual employee details.
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl p-6 md:p-8 bg-white/10 backdrop-blur-sm border border-white/15">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
                <h3 className="font-heading text-xl tracking-tight">Employers may see</h3>
              </div>
              <ul className="space-y-2.5 text-sm text-white/90">
                {CAN_SEE.map((c) => (
                  <li key={c} className="flex items-start gap-2">
                    <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-emerald-300" aria-hidden="true" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl p-6 md:p-8 bg-white/10 backdrop-blur-sm border border-white/15">
              <div className="flex items-center gap-2 mb-4">
                <EyeOff className="w-5 h-5" strokeWidth={2} aria-hidden="true" />
                <h3 className="font-heading text-xl tracking-tight">Employers do not see</h3>
              </div>
              <ul className="space-y-2.5 text-sm text-white/90">
                {CANNOT_SEE.map((c) => (
                  <li key={c} className="flex items-start gap-2">
                    <span className="mt-1.5 shrink-0 w-1.5 h-1.5 rounded-full bg-rose-300" aria-hidden="true" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <p className="mt-10 text-sm text-white/75 max-w-3xl leading-relaxed">
            CALLACHAP is non-clinical chaplain support and does not replace emergency services,
            medical care, mental-health treatment, or an Employee Assistance Program.
          </p>
        </div>
      </section>

      {/* ============================ MULTI-ORG ============================ */}
      <section className="py-16 md:py-24 border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold mb-3">
              One CCCHAPS platform
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">
              Support multiple organizations. Centrally managed by CCCHAPS.
            </h2>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              Organization branding, available support methods, resource links, and routing rules
              can eventually be configured per organization while CALLACHAP remains centrally
              managed by California Corporate Chaplains.
            </p>
          </div>
          <div
            data-testid="callachap-orgs-grid"
            className="mt-10 grid sm:grid-cols-3 gap-4"
          >
            {ORGS.map((o) => (
              <div
                key={o.name}
                className={`rounded-2xl p-6 border transition-all ${
                  o.accent
                    ? "bg-white border-[#4A6FA5] shadow-md ring-2 ring-[#4A6FA5]/10"
                    : "bg-white border-slate-200"
                }`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Building2 className="w-5 h-5 text-[#4A6FA5]" strokeWidth={2} aria-hidden="true" />
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-500">
                    Organization
                  </span>
                </div>
                <h3 className="font-heading text-2xl tracking-tight text-slate-900">{o.name}</h3>
                <p className={`mt-2 text-xs uppercase tracking-[0.2em] font-semibold ${o.accent ? "text-[#4A6FA5]" : "text-slate-500"}`}>
                  {o.tag}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ SAFETY NOTICE ============================ */}
      <section
        data-testid="callachap-safety-notice"
        className="py-10 border-t border-slate-100"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-3 p-5 rounded-xl bg-[#FFF8ED] border border-[#F5D68A]/60">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-[#B8791A]" strokeWidth={2} aria-hidden="true" />
            <p className="text-sm text-[#7A4F0F] leading-relaxed">
              CALLACHAP provides workplace chaplain support and is not an emergency, medical,
              psychiatric, or crisis-response service. If you or another person is in immediate
              danger or experiencing an emergency, contact 911 or the appropriate emergency
              service.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
